import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/typography";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CurrencyIcon } from "@/components/ui/CurrencyIcon";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAdminPayouts, useAdminTransactions } from '@/hooks/admin';
import { supabaseBrowser as supabase } from '@/lib/supabase-browser';
import { callRpcList } from "@/integrations/supabase/rpc";
import { AdminCustomers, AdminSecurity } from '@/lib/rpc/admin-api';
import { getCurrentSession } from '@/lib/rpc/auth-facade';
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Link, useParams } from "react-router-dom";
import { toast } from '@/hooks/use-toast';

const fmt = (d?: string | null) => (d ? new Date(d).toLocaleString() : "—");
const short = (s?: string | null) => (s ? `${s.slice(0, 6)}…${s.slice(-4)}` : "—");
const exportCsv = (filename: string, rows: any[], columns: { key: string; header: string }[]) => {
  const esc = (v: any) => {
    if (v == null) return "";
    const s = String(v).replace(/"/g, '""');
    return `"${s}"`;
  };
  const header = columns.map(c => esc(c.header)).join(",");
  const lines = rows.map(r => columns.map(c => esc((r as any)[c.key])).join(","));
  const csv = [header, ...lines].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const BridgeCustomer = () => {
  const { t } = useTranslation('common');
  const { bridge_customer_id } = useParams();
  const [authed, setAuthed] = useState(false);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    document.title = `Bridge ${bridge_customer_id} — Supabank`;
    const desc = document.querySelector('meta[name="description"]');
    const content = `Bridge customer ${bridge_customer_id} details and activity`;
    if (desc) desc.setAttribute("content", content);
    else {
      const m = document.createElement("meta");
      m.name = "description";
      m.content = content;
      document.head.appendChild(m);
    }

    const link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const href = `${window.location.origin}/admin/bridge/${bridge_customer_id}`;
    if (link) link.href = href; else {
      const l = document.createElement("link");
      l.rel = "canonical"; l.href = href; document.head.appendChild(l);
    }
  }, [bridge_customer_id]);

  useEffect(() => {
    const init = async () => {
      const session = await getCurrentSession();
      const uid = session?.user?.id ?? null;
      if (!uid) { setAuthed(false); setIsAdmin(null); return; }
      setAuthed(true);
      try {
        const result = await AdminSecurity.isAdminSimple();
        if (result.error) throw result.error;
        setIsAdmin(!!result.data);
      } catch {
        setIsAdmin(false);
      }
    };
    init();
  }, []);

  const customerQuery = useQuery({
    queryKey: ["bridge-customer", bridge_customer_id],
    enabled: authed && isAdmin === true && !!bridge_customer_id,
    queryFn: async () => {
      if (!bridge_customer_id) throw new Error('bridge_customer_id is required');
      // Prefer internal table, fallback to external mirror
      const { data: internalList, error: e1 } = await AdminCustomers.getByIdentifier(bridge_customer_id);
      if (e1) throw e1;
      const internal = Array.isArray(internalList)
        ? (internalList as any[]).find((r: any) => r.bridge_customer_id === bridge_customer_id) ?? (internalList as any[])[0]
        : null;
      if (internal) return { source: "internal", record: internal } as const;

      // No external fallback needed - use unified view only
      return { source: "external", record: null } as const;
    },
  });

  const addressesQuery = useQuery({
    queryKey: ["bridge-addresses", bridge_customer_id],
    enabled: authed && isAdmin === true && !!bridge_customer_id,
    queryFn: async () => {
      const result = await callRpcList('admin_list_liquidation_addresses_by_bridge_id', { _bridge_customer_id: bridge_customer_id });
      return result.data || [];
    },
  });

  // Hook-based payouts data
  const payoutsQuery = useAdminPayouts(
    { limit: 200, offset: 0 },
    { key: 'settled_at', dir: 'desc' },
    { bridgeCustomerId: bridge_customer_id }
  );

  // Hook-based transactions data
  const transactionsQuery = useAdminTransactions({
    limit_param: 200,
    customer_filter: bridge_customer_id || undefined,
  });

  const badgeFor = (status?: string | null) => {
    const s = (status || "").toLowerCase();
    let variant: any = "outline";
    if (["approved", "settled", "completed", "valid", "active", "success", "ok"].includes(s)) variant = "default";
    else if (["pending", "processing", "review_required"].includes(s)) variant = "secondary";
    else if (["rejected", "failed", "invalid", "error"].includes(s)) variant = "destructive";
    return <Badge variant={variant}>{status || "—"}</Badge>;
  };

  const rec: any = (customerQuery.data as any)?.record || null;
  const userId: string | null = rec?.user_id || null;
  const customerId: string | null = rec?.id || null;

  // 🔄 REALTIME SUBSCRIPTIONS - Auto-refresh on Bridge webhook events
  useEffect(() => {
    if (!customerId) return;
    
    // Subscribe to customer table updates (KYC status, endorsements, etc.)
    const customerChannel = supabase
      .channel(`bridge-customer-${customerId}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'customers',
        filter: `id=eq.${customerId}`
      }, (payload) => {
        console.log('[BridgeCustomer] Customer updated via webhook:', payload.new);
        queryClient.invalidateQueries({ queryKey: ['bridge-customer', bridge_customer_id] });
        
        toast.success('Customer data updated', {
          description: 'Bridge webhook received - data refreshed automatically'
        });
      })
      .subscribe();

    console.log('[BridgeCustomer] Subscribed to customer realtime updates:', customerId);

    return () => {
      console.log('[BridgeCustomer] Unsubscribing from customer updates');
      customerChannel.unsubscribe();
    };
  }, [customerId, bridge_customer_id]); // ✅ Removed queryClient from deps

  // Subscribe to liquidation addresses updates
  useEffect(() => {
    if (!customerId) return;
    
    const addressesChannel = supabase
      .channel(`bridge-addresses-${customerId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'liquidation_addresses',
        filter: `customer_id=eq.${customerId}`
      }, (payload) => {
        console.log('[BridgeCustomer] Liquidation address updated via webhook:', payload);
        queryClient.invalidateQueries({ queryKey: ['bridge-addresses', bridge_customer_id] });
        
        toast.success('Liquidation addresses updated', {
          description: 'Bridge webhook received - addresses refreshed'
        });
      })
      .subscribe();

    console.log('[BridgeCustomer] Subscribed to liquidation addresses updates');

    return () => {
      addressesChannel.unsubscribe();
    };
  }, [customerId, bridge_customer_id]); // ✅ Removed queryClient from deps

  // Subscribe to transactions updates (tx table)
  useEffect(() => {
    if (!userId) return;
    
    const txChannel = supabase
      .channel(`bridge-transactions-${userId}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'tx',
        filter: `user_id=eq.${userId}`
      }, (payload) => {
        console.log('[BridgeCustomer] Transaction updated via webhook:', payload.new);
        queryClient.invalidateQueries({ queryKey: ['admin-transactions'] });
        
        toast.success('Transaction status updated', {
          description: 'Real-time update from Bridge webhook'
        });
      })
      .subscribe();

    console.log('[BridgeCustomer] Subscribed to transaction updates');

    return () => {
      txChannel.unsubscribe();
    };
  }, [userId]); // ✅ Removed queryClient from deps - it's stable

  return (
    <main className="container mx-auto px-4 py-6">
      <header className="mb-4">
        <Heading level={1}>Bridge Customer</Heading>
        <p className="opacity-70 text-sm">{bridge_customer_id}</p>
      </header>

      <div className="grid grid-cols-1 gap-4">
        <Card>
          <CardHeader><CardTitle>Overview</CardTitle></CardHeader>
          <CardContent>
            {rec ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <div className="text-sm opacity-60">Email</div>
                  <div>{rec.email || "—"}</div>
                </div>
                <div>
                  <div className="text-sm opacity-60">Type</div>
                  <div>{rec.type || "—"}</div>
                </div>
                <div>
                  <div className="text-sm opacity-60">KYC</div>
                  <div>{badgeFor(rec.kyc_status)}</div>
                </div>
                <div>
                  <div className="text-sm opacity-60">TOS</div>
                  <div>{rec.tos_status || "—"}</div>
                </div>
                <div>
                  <div className="text-sm opacity-60">Linked User</div>
                  <div>{userId ? <Link to={`/admin/user/${userId}`} className="underline-offset-2 hover:underline">{short(userId)}</Link> : "—"}</div>
                </div>
                <div>
                  <div className="text-sm opacity-60">Updated</div>
                  <div>{fmt(rec.updated_at)}</div>
                </div>
              </div>
            ) : (
              <div>{t('loading', 'Loading...')}</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Liquidation Addresses</CardTitle>
              <Button variant="secondary" onClick={() => exportCsv(`addresses_${bridge_customer_id}`, (addressesQuery.data || []), [
                { key: "user_id", header: "User" },
                { key: "address", header: "Address" },
                { key: "chain", header: "Chain" },
                { key: "asset", header: "Asset" },
                { key: "currency", header: "Currency" },
                { key: "status", header: "Status" },
                { key: "created_at", header: "Created" },
              ])}>Export</Button>
            </div>
          </CardHeader>
          <CardContent>
            {addressesQuery.isLoading ? (
              <div className="p-4 text-sm opacity-70">Loading…</div>
            ) : (addressesQuery as any).error ? (
              <div className="p-4 text-sm text-destructive">Error: {String((addressesQuery as any).error?.message || (addressesQuery as any).error)}</div>
            ) : (addressesQuery.data || []).length === 0 ? (
              <div className="p-4 text-sm opacity-70">No addresses</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Address</TableHead>
                      <TableHead>Chain</TableHead>
                      <TableHead>Asset</TableHead>
                      <TableHead>Currency</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(addressesQuery.data || []).map((a: any) => (
                      <TableRow key={a.id}>
                        <TableCell>{a.user_id ? <Link to={`/admin/user/${a.user_id}`} className="underline-offset-2 hover:underline">{short(a.user_id)}</Link> : "—"}</TableCell>
                        <TableCell className="font-medium">{a.address}</TableCell>
                        <TableCell>{a.chain}</TableCell>
                        <TableCell>{a.asset}</TableCell>
                        <TableCell>
                          {a.currency ? (
                            <div className="flex items-center gap-1.5">
                              <CurrencyIcon symbol={a.currency} size="xs" className="shrink-0" />
                              {a.currency}
                            </div>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>{a.status || "—"}</TableCell>
                        <TableCell>{fmt(a.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Transactions</CardTitle>
              <Button variant="secondary" onClick={() => exportCsv(`transactions_${bridge_customer_id}`, (transactionsQuery.transactions || []), [
                { key: "occurred_at", header: "Time" },
                { key: "user_id", header: "User" },
                { key: "type", header: "Type" },
                { key: "direction", header: "Direction" },
                { key: "asset", header: "Asset" },
                { key: "chain", header: "Chain" },
                { key: "currency", header: "Currency" },
                { key: "amount_out", header: "Gross Fiat" },
                { key: "fee_dev", header: "Dev Fee" },
                { key: "status", header: "Status" },
                { key: "bridge_tx_id", header: "Ref" },
              ])}>Export</Button>
            </div>
          </CardHeader>
          <CardContent>
            {transactionsQuery.loading ? (
              <div className="p-4 text-sm opacity-70">Loading…</div>
            ) : transactionsQuery.error ? (
              <div className="p-4 text-sm text-destructive">Error: {String((transactionsQuery.error as any)?.message || transactionsQuery.error)}</div>
            ) : (transactionsQuery.transactions || []).length === 0 ? (
              <div className="p-4 text-sm opacity-70">No transactions</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Direction</TableHead>
                      <TableHead>Asset</TableHead>
                      <TableHead>Chain</TableHead>
                      <TableHead>Currency</TableHead>
                      <TableHead>Gross Fiat</TableHead>
                      <TableHead>Dev Fee</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Ref</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(transactionsQuery.transactions || []).map((t: any) => (
                      <TableRow key={t.id}>
                        <TableCell>{fmt(t.occurred_at)}</TableCell>
                        <TableCell>{t.user_id ? <Link to={`/admin/user/${t.user_id}`} className="underline-offset-2 hover:underline">{short(t.user_id)}</Link> : "—"}</TableCell>
                        <TableCell>{t.type || "—"}</TableCell>
                        <TableCell>{t.direction || "—"}</TableCell>
                        <TableCell>{t.asset || "—"}</TableCell>
                        <TableCell>{t.chain || "—"}</TableCell>
                        <TableCell>
                          {t.currency ? (
                            <div className="flex items-center gap-1.5">
                              <CurrencyIcon symbol={t.currency} size="xs" className="shrink-0" />
                              {t.currency}
                            </div>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>{t.amount_out != null ? String(t.amount_out) : "—"}</TableCell>
                        <TableCell>{t.fee_dev != null ? String(t.fee_dev) : "—"}</TableCell>
                        <TableCell>{t.status || "—"}</TableCell>
                        <TableCell>{short(t.bridge_tx_id)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Payouts</CardTitle>
              <Button variant="secondary" onClick={() => exportCsv(`payouts_${bridge_customer_id}`, (payoutsQuery.rows || []), [
                { key: "settled_at", header: "Settled" },
                { key: "user_id", header: "User" },
                { key: "amount", header: "Amount" },
                { key: "currency", header: "Currency" },
                { key: "fee_dev", header: "Dev Fee" },
                { key: "fee_total", header: "Total Fee" },
                { key: "status", header: "Status" },
                { key: "payout_id", header: "Payout ID" },
              ])}>Export</Button>
            </div>
          </CardHeader>
          <CardContent>
            {payoutsQuery.isLoading ? (
              <div className="p-4 text-sm opacity-70">Loading…</div>
            ) : payoutsQuery.isError ? (
              <div className="p-4 text-sm text-destructive">Error: {String((payoutsQuery.error as any)?.message || payoutsQuery.error)}</div>
            ) : (payoutsQuery.rows || []).length === 0 ? (
              <div className="p-4 text-sm opacity-70">No payouts</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Settled</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Currency</TableHead>
                      <TableHead>Dev Fee</TableHead>
                      <TableHead>Total Fee</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Payout ID</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(payoutsQuery.rows || []).map((p: any) => (
                      <TableRow key={p.id}>
                        <TableCell>{fmt(p.settled_at)}</TableCell>
                        <TableCell>{p.user_id ? <Link to={`/admin/user/${p.user_id}`} className="underline-offset-2 hover:underline">{short(p.user_id)}</Link> : "—"}</TableCell>
                        <TableCell>{p.amount != null ? String(p.amount) : "—"}</TableCell>
                        <TableCell>
                          {p.currency ? (
                            <div className="flex items-center gap-1.5">
                              <CurrencyIcon symbol={p.currency} size="xs" className="shrink-0" />
                              {p.currency}
                            </div>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>{p.fee_dev != null ? String(p.fee_dev) : "—"}</TableCell>
                        <TableCell>{p.fee_total != null ? String(p.fee_total) : "—"}</TableCell>
                        <TableCell>{p.status || "—"}</TableCell>
                        <TableCell>{short(p.payout_id)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  );
};

export default BridgeCustomer;
